import { useState, useEffect, useCallback } from "react";
import { adminAPI, APIError } from "../lib/api";
import type { WaitlistSignup } from "../types/api";
import styles from "./Waitlist.module.css";

type SortField = "email" | "created_at";
type SortOrder = "asc" | "desc";

export function Waitlist() {
  const [signups, setSignups] = useState<WaitlistSignup[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortField>("created_at");
  const [order, setOrder] = useState<SortOrder>("desc");
  const [page, setPage] = useState(0);
  const limit = 50;

  const fetchSignups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminAPI.listWaitlist({
        sort,
        order,
        limit,
        offset: page * limit,
      });
      setSignups(data.signups);
      setTotal(data.total);
    } catch (err) {
      if (err instanceof APIError) {
        setError(err.message);
      } else {
        setError("Failed to load waitlist signups");
      }
    } finally {
      setLoading(false);
    }
  }, [sort, order, page]);

  useEffect(() => {
    fetchSignups();
  }, [fetchSignups]);

  const handleSort = (field: SortField) => {
    if (sort === field) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSort(field);
      setOrder(field === "created_at" ? "desc" : "asc");
    }
    setPage(0);
  };

  const sortIndicator = (field: SortField) => {
    if (sort !== field) return "";
    return order === "asc" ? " ↑" : " ↓";
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Waitlist Signups</h1>
          <p className={styles.subtitle}>
            {total} {total === 1 ? "signup" : "signups"} collected
          </p>
        </div>
      </header>

      {loading && <div className={styles.loading}>Loading signups...</div>}

      {error && <div className={styles.error}>{error}</div>}

      {!loading && !error && signups.length === 0 && (
        <div className={styles.empty}>No waitlist signups yet.</div>
      )}

      {!loading && !error && signups.length > 0 && (
        <>
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th
                  className={styles.sortable}
                  onClick={() => handleSort("email")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") handleSort("email");
                  }}
                  tabIndex={0}
                >
                  Email{sortIndicator("email")}
                </th>
                <th
                  className={styles.sortable}
                  onClick={() => handleSort("created_at")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") handleSort("created_at");
                  }}
                  tabIndex={0}
                >
                  Signed Up{sortIndicator("created_at")}
                </th>
              </tr>
            </thead>
            <tbody>
              {signups.map((signup) => (
                <tr key={signup.id} className={styles.tableRow}>
                  <td className={styles.email}>{signup.email}</td>
                  <td className={styles.date}>{formatDate(signup.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <span className={styles.paginationInfo}>
                Page {page + 1} of {totalPages} ({total} total)
              </span>
              <div className={styles.paginationButtons}>
                <button
                  className={styles.paginationButton}
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page === 0}
                >
                  Previous
                </button>
                <button
                  className={styles.paginationButton}
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= totalPages - 1}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
